import { useEffect, useRef, useState } from 'react'
import { router } from '@inertiajs/react'
import {
  openBusinessProcessEditChat,
  subscribeBusinessProcessDraftPreview,
  subscribeBusinessProcessRevised,
} from '@/frontend/features/AgentChats/assistant-ui/businessProcessEditChatBus'
import type { ProcedurePresentation } from '@/frontend/features/AgentChats/events/agentEvents'
import ProcessAdvancedSection from './ProcessAdvancedSection'
import ProcessActionPanel from './ProcessActionPanel'
import ProcessHeader from './ProcessHeader'
import ProcessHumanSummary from './ProcessHumanSummary'
import ProcessWorkflowSection from './ProcessWorkflowSection'
import type { BusinessProcessDetail, EditChatWorker } from './types'

interface Props {
  page_title: string
  process: BusinessProcessDetail
  // Página de operação (execuções/rotinas/memória) da Onda C. Esta Show é a
  // gestão do ciclo de vida; a operação vive na rota worker-scoped.
  operations_path?: string
  // Colaborador responsável para a edição por conversa no widget (SZ-1708).
  edit_chat?: EditChatWorker
}

export default function BusinessProcessShow({ page_title, process, operations_path, edit_chat }: Props) {
  const [draftPreview, setDraftPreview] = useState<ProcedurePresentation | null>(null)
  const [deleting, setDeleting] = useState(false)
  const activeReloadCancelRef = useRef<(() => void) | null>(null)
  const agentActive = ['document_generating', 'skill_generating', 'publication_pending'].includes(process.progress_kind)

  useEffect(() => {
    if (!agentActive || deleting) return

    const intervalId = window.setInterval(() => {
      // A resposta anterior pode demorar mais que o intervalo. Não sobreponha
      // reloads: além de carga desnecessária, uma resposta tardia pode disputar
      // com navegações destrutivas como a exclusão deste processo.
      if (activeReloadCancelRef.current) return

      router.reload({
        only: ['process'],
        onCancelToken: ({ cancel }) => { activeReloadCancelRef.current = cancel },
        onFinish: () => { activeReloadCancelRef.current = null },
      })
    }, 3000)

    return () => {
      window.clearInterval(intervalId)
      activeReloadCancelRef.current?.()
      activeReloadCancelRef.current = null
    }
  }, [deleting, process.id, process.progress_kind])

  const pauseReloadsForDelete = () => {
    activeReloadCancelRef.current?.()
    activeReloadCancelRef.current = null
    setDeleting(true)
  }

  // Cada revisão aplicada pelo intake (widget de edição) atualiza `at` na
  // sessão; recarrega o workflow DESTE processo a cada aplicação, mantendo a
  // posição de leitura. O widget vive no layout persistente e sobrevive ao
  // reload do Inertia.
  useEffect(() => subscribeBusinessProcessRevised(({ processId }) => {
    if (processId !== process.id) return

    // router.reload já preserva scroll e estado por padrão.
    router.reload({ only: ['process'] })
  }), [process.id])

  useEffect(() => subscribeBusinessProcessDraftPreview(({ processId, procedure }) => {
    if (processId === process.id) setDraftPreview(procedure)
  }), [process.id])

  // Deep-link `/edit` → show com `?edit_chat=1`: abre a conversa de edição
  // uma única vez e tira a flag da URL para um refresh não reabrir sozinho.
  const editChatAutoOpenedRef = useRef(false)
  const canEditChat = Boolean(process.can_edit && edit_chat?.chat_ready && edit_chat?.intake_enabled)
  useEffect(() => {
    if (editChatAutoOpenedRef.current || !canEditChat || !edit_chat) return

    const params = new URLSearchParams(window.location.search)
    if (params.get('edit_chat') !== '1') return

    editChatAutoOpenedRef.current = true
    params.delete('edit_chat')
    const query = params.toString()
    window.history.replaceState(window.history.state, '', `${window.location.pathname}${query ? `?${query}` : ''}`)
    openBusinessProcessEditChat({ processId: process.id, workerId: edit_chat.id })
  }, [canEditChat, edit_chat, process.id])

  return (
    <div className="space-y-6">
      <ProcessHeader
        pageTitle={page_title}
        process={process}
        operationsPath={operations_path}
        editChat={edit_chat}
        onDeleteStart={pauseReloadsForDelete}
        onDeleteFailed={() => setDeleting(false)}
      />

      <ProcessActionPanel process={process} />

      <ProcessWorkflowSection steps={process.business_steps} draftPreview={draftPreview} />

      <ProcessHumanSummary process={process} />

      {process.can_view_technical_details && <ProcessAdvancedSection process={process} />}

    </div>
  )
}
